import { Injectable } from '@angular/core';
import { ProjectListType } from '@project/shared-projects-list/project-list-type.enum';
import { ColumnConfigFactory } from '@project/shared/grid/column-config-factory';
import { ProjectType } from '@project/shared/project-type.enum';
import { BaseAgGridSettingsService } from '@shared/ag-grid/base-ag-grid-settings.service';
import { DateFilterComponent } from '@shared/ag-grid/component/date-filter/date-filter.component';
import { GridState } from '@shared/ag-grid/gird-state';
import { AgGridServicesWrapperService } from '@shared/ag-grid/services-wrapper.service';
import { Organization } from '@shared/models/organization/organization.model';
import { UtilityService } from '@shared/services/utility.service';
import { UserService } from '@user/user.service';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ReferenceType } from '@shared/references.model';
import { ProjectStatusColors } from '@project/shared/project-status/status-styles.enum';

@Injectable()
export class MyProjectGridSettingService extends BaseAgGridSettingsService {
  private column = [];
  private projectTypes = {};

  public columnValues = [];
  public operatorValues = [];
  public typeValues = [
    'User Specified',
    'Fill from column'
  ];
  public currentGridState: GridState = new GridState();
  public initialGridState: GridState = new GridState();
  public defaultGridState: GridState = new GridState();
  public _gridStateStorageKey: string;
  public _gridApi: GridApi;
  public _columnApi: ColumnApi;
  private userOrg: Organization;
  private userService: UserService;

  constructor(_agGridServicesWrapperService: AgGridServicesWrapperService,
              private _utilityService: UtilityService,
              private readonly translateService: TranslateService,
              private _columnConfigFactory: ColumnConfigFactory) {
    super(_agGridServicesWrapperService);
    this.userService = _agGridServicesWrapperService._userService;
    this.userService.getCurrentUser()
      .pipe(take(1))
      .subscribe((user) => {
        this.userOrg = user.organization;
      });
    this.column = _agGridServicesWrapperService._translateService.instant('projects.column');
    this.projectTypes = _agGridServicesWrapperService._translateService.instant('project.types');
  }

  private getPageByProjectType(listType: ProjectListType, params, icon: boolean) {
    let page = 'planning/summary';
    if (params.data?.projectType === ProjectType.ci) {
      page = 'overview';
    }
    if (listType === ProjectListType.CLAIMS) {
      page = 'connections';
    }
    if (params?.data) {
      if (icon) {
        return `<a class="hyperlink" href="#/oes/projects/my-projects/${params.data.id}/${page}" title="Open ${params.data.name}"><i class="fa fa-arrow-circle-o-right" aria-hidden="true"></i></a>`;
      }
      return `<a class="hyperlink" href="#/oes/projects/my-projects/${params.data.id}/${page}" title="Open ${params.data.name}">${params.data.name}</a><p style="margin: 0;">${this.projectTypes[params.data?.projectType]}</p>`;
    }
  }

  public getGridOptions(listType: ProjectListType, projectStatuses: any[], initColumns = true): GridOptions {
    return <GridOptions>{
      columnDefs: initColumns ? this.getColumnSetting(listType, projectStatuses) : [],
      defaultColDef: {
        floatingFilter: true,
        sortable: true,
        filter: true
      },
      suppressRowClickSelection: true,
      rowMultiSelectWithClick: false,
      groupHideOpenParents: true,
      groupSelectsChildren: true,
      rowSelection: 'multiple',
      singleClickEdit: true,
      getRowHeight: (params) => {
        if (params.node.rowHeight > 80) {
          return params.node.rowHeight + 10;
        }
        return 80;
      },
      rowClass: 'row-with-tags',
      suppressDragLeaveHidesColumns: false,
      rowGroupPanelShow: 'always',
      enableRangeSelection: true,
      autoGroupColumnDef: {
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          checkbox: true
        }
      },
      overlayLoadingTemplate: '<div class="loading-ring loading-ring-small"><div></div><div></div><div></div><div></div></div>',
      sideBar: {
        toolPanels: ['columns']
      },
      // to access 'this' from cellRenderer/valueFormatter
      context: {
        currentLang: this.currentLang
      },
      components: {
        agDateInput: DateFilterComponent
      },
      localeText: this.globalTranslations
    };
  }

  // Column Properties
  // https://www.ag-grid.com/javascript-grid-column-properties/
  public getColumnSetting(listType: ProjectListType, projectStatuses: any[]): any {
    const columnConfigs = this._columnConfigFactory;
    const colDefs = [
      columnConfigs.get('checkbox'),
      {
        headerName: this.column['project-name'],
        headerTooltip: this.column['project-name'],
        field: 'name',
        width: 300,
        unSortIcon: true,
        enablePivot: true,
        enableRowGroup: true,
        enableValue: true,
        pinned: 'left',
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          return this.getPageByProjectType(listType, params, false);
        },
        comparator: this.caseInsensitiveSort,
        filter: 'agTextColumnFilter',
        filterParams: {
          clearButton: true
        },
        resizable: true
      },
      {
        headerName: this.column['stage'],
        field: 'projectStages',
        headerTooltip: this.column['stage'],
        unSortIcon: true,
        enablePivot: true,
        enableRowGroup: true,
        enableValue: true,
        filter: 'agSetColumnFilter',
        comparator: (a, b) => {
          try {
            const statusesA: any = JSON.parse(a);
            const orderedStatusesA = this._utilityService.orderJSONStatusesByDate(statusesA)
              .filter(status => status.timestamp != null)
              .map(status => status.name);

            const statusesB: any = JSON.parse(b);
            const orderedStatusesB = this._utilityService.orderJSONStatusesByDate(statusesB)
              .filter(status => status.timestamp != null)
              .map(status => status.name);

            const latestStatusA = orderedStatusesA ? orderedStatusesA[orderedStatusesA.length - 1] : null;
            const latestStatusB = orderedStatusesB ? orderedStatusesB[orderedStatusesB.length - 1] : null;

            return this.caseInsensitiveSort(latestStatusA, latestStatusB);
          } catch (jsonParseError: any) {
          }
        },
        filterParams: {
          clearButton: true,
          valueGetter: (params) => {
            const statuses: any = JSON.parse(params.data.projectStages);
            const orderedStatuses = this._utilityService.orderJSONStatusesByDate(statuses)
              .filter(status => status.timestamp != null)
              .map(status => status.name);

            if (orderedStatuses?.length) {
              return orderedStatuses[orderedStatuses.length - 1];
            }
            return null;
          }
        },
        suppressSizeToFit: false,
        cellRenderer: (params) => {
          if (params?.value) {
            const statuses: any = JSON.parse(params.value);
            const orderedStatuses = this._utilityService.orderJSONStatusesByDate(statuses);
            const currentStatus = orderedStatuses && orderedStatuses[orderedStatuses.length - 1]?.timestamp ? orderedStatuses[orderedStatuses.length - 1] : null;
            if (!currentStatus) {
              return '';
            }
            // Display status badge without caret - modal doesn't work for My Projects list
            return `<div style="display: flex; align-items: center;">
                      <div style="width: fit-content;
                            display: flex;
                            border-radius: 8px;
                            line-height: 0;
                            align-items: center;
                            justify-content: center;
                            padding: 0 11px;
                            height: 17px;
                            color: ${this._utilityService.findTextColor(currentStatus.color)};
                            background-color: ${currentStatus.color};">
                         ${currentStatus.name}
                      </div>
                    </div>`;
          }
        },
        hide: this.userOrg?.financier
      },
      columnConfigs.get('submittedToProgramDateTime'),
      {
        ...columnConfigs.get('status'),
        editable: true,
        hide: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: projectStatuses.map(status => {
            return status.id;
          })
        },
        valueFormatter: (params) => {
          const status: ReferenceType = projectStatuses.find(s => s.id === params.value);
          return status ? this.translateService.instant(status.code) : params.value;
        },
        cellRenderer: (params) => {
          if (params?.value) {
            const projectStatus: ReferenceType = projectStatuses.find(s => s.id === params.value);
            return `<div style="display: flex; align-items: center;">
              <div style="width: fit-content;
                    display: flex;
                    border-radius: 16px;
                    line-height: 0;
                    align-items: center;
                    justify-content: center;
                    padding: 4px 12px;
                    height: 28px;
                    background-color: ${ProjectStatusColors[projectStatus.id]};">
                  ${this.translateService.instant(projectStatus.code)}
                  <img
                    style="width: 10px; margin-left: 6px;"
                    src="./assets/icons/line-icons/shared/grey/arrow-down.svg"
                  />
              </div>
            </div>`;
          }
        },
        resizable: true
      },
      columnConfigs.get('projectType'),
      columnConfigs.get('latitude'),
      columnConfigs.get('longitude'),
      columnConfigs.get('country'),
      columnConfigs.get('region'),
      columnConfigs.get('totalCustomers'),
      columnConfigs.get('totalPV'),
      columnConfigs.get('storage'),
      columnConfigs.get('generator'),
      columnConfigs.get('totalCapex'),
      columnConfigs.get('capexPerConnection'),
      columnConfigs.get('subsidyAmountPerConnection'),
      columnConfigs.get('lcoe'),
      columnConfigs.get('irr'),
      columnConfigs.get('equityIRR'),
      columnConfigs.get('payback'),
      columnConfigs.get('equityPayback'),
      columnConfigs.get('cod'),
      columnConfigs.get('submittedToProgramDateTime'),
      columnConfigs.get('arpuResidential'),
      columnConfigs.get('arpuCommercial'),
      columnConfigs.get('arpuPublic'),
      columnConfigs.get('arpuProductive'),
      columnConfigs.get('arpuAnchor')
    ];

    switch (listType) {
      case ProjectListType.ASSETS:
        break;
      case ProjectListType.CLAIMS:
        break;
      case ProjectListType.MAIN:
        colDefs.splice(3, 0, columnConfigs.get('program'));
        colDefs.splice(6, 0, columnConfigs.get('latestCompletedMilestone'));
        break;
      default:
        break;
    }

    return colDefs;
  }
}
